import {
  hoursToSeconds,
  isToday,
  isTomorrow,
  millisecondsToSeconds,
} from 'date-fns';
import { format as formatTZ, getTimezoneOffset } from 'date-fns-tz';
import { mapValues } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { useDateLang } from '~/hooks/useDateLang';
import { truncateSlots } from '~/pages/Checkout/truncateSlots';

import { cartStore, checkoutStore, orderStore } from '~/stores';

export const useDeliverySlotsInfo = () => {
  const currentDelivery = checkoutStore.slotSelectionModalStore.selectedSlot;

  const currentItems = cartStore.selectedCartItems.filter(({ sku }) =>
    currentDelivery?.itemsIds.includes(sku),
  );

  const isExpressDeliveryAvailable = currentItems.every(
    ({ count, sellableWarehouses }) => {
      const currentWHStock = Number(
        sellableWarehouses[`${orderStore.etaCalculation?.warehouse.code}`] || 0,
      );

      return count <= currentWHStock;
    },
  );

  const latestBackorderLeadTime =
    currentItems.sort((a, b) => a.backorderLeadTime - b.backorderLeadTime).at(0)
      ?.backorderLeadTime ?? 0;

  const now = millisecondsToSeconds(Date.now());
  const latestBackorderTime = now + hoursToSeconds(latestBackorderLeadTime);

  const deliverySlots = truncateSlots(
    mapValues(checkoutStore.deliverySlots, (slots) =>
      slots.filter(
        ({ delivery_min_time }) => delivery_min_time >= latestBackorderTime,
      ),
    ),
  );

  return {
    currentDelivery,
    currentItems,
    isExpressDeliveryAvailable,
    latestBackorderLeadTime,
    latestBackorderTime,
    deliverySlots,
    now,
  };
};

export const useDateItem = ({ timeStamp }: { timeStamp: string }) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();

  const offset = getTimezoneOffset(company.config.timeZone);
  const timeStampOffseted = +timeStamp + offset;

  let day = formatTZ(new Date(timeStampOffseted), 'd MMMM', {
    timeZone: company.config.timeZone,
    locale,
  });
  if (isToday(+timeStampOffseted)) {
    day = t('today');
  }
  if (isTomorrow(+timeStampOffseted)) {
    day = t('tomorrow');
  }

  return {
    day,
    offset,
    timeStampOffseted,
  };
};
